import { useRef } from "react";
import { useForm } from "react-hook-form";
import moment from "moment";
import { useCreateOrderMutation } from "../../../../../../../../API/authApi";
import { useToastify } from "../../../../../../../../Hooks/hooks";
import useDialogHooks from "../../../../../../../../Components/Dialog/dialog-hooks";
import { IOrderDeliveryForm } from "../../../../../../../../Interfaces/formValue-interfaces";
import { INewOrder } from "../../../../../../../../Interfaces/request-interfaces";
import { IDeliveryOrderData, IOrderParams, OrderTypes } from "../../../../../../../../Interfaces/componentTypes";
import { IDeliveryOrderProps } from "./orderDeliveryDialog";
import useLanguagesHooks from "../../../../../../../../Components/Languages/setLanguage-hooks";
import SmsCodeDialog from "../SMSCode/smsCodeDialog";
import OrderSuccess from "../OrderSuccess/orderSuccess";
import { useOrderPayment } from "../../OrderPayment/useOrderPayment";
import { getOrderTimeValidationMessageKey, getNextOrderDate, isBusinessClosed, isBusinessFreeDay, validateOrderDateTime } from "../../../../../../../../Utilits/utilits";
import useOrderScheduleValidation from "../../OrderScheduleNotices/useOrderScheduleValidation";

const useOrderDelivery = (props: IDeliveryOrderProps) => {
    const { companyId, company_name, company_logo, company_contacts, cart, deliveryMinCost, packageFee, totalPrice, pos_terminal, setDialog, onCloseDialog, setMainCart, totalItemCount, setContentType, business_profile } = props;
    const [sendOrder, { isLoading: newOrderLoading }] = useCreateOrderMutation()
    const user_id = localStorage.getItem('user_id');
    const phone = localStorage.getItem('phone');
    const { open, onOpen: onOpenNoteDialog, onClose: onCloseNoteDialog } = useDialogHooks();
    const currentDate = new Date();
    const dialogRef = useRef<HTMLDivElement>(null);
    const { t } = useLanguagesHooks();
    const { showToastError } = useToastify();
    const isScheduleRequired = isBusinessClosed(business_profile);

    const calculatedDeliveryCost = Number(deliveryMinCost) || 0;
    const normalizedPackageFee = Number(packageFee) || 0;
    const normalizedTotalPrice = Number(totalPrice) || 0;

    const {
        register,
        watch,
        control,
        formState: { errors },
        getValues,
        setValue,
        setError,
        clearErrors,
    } = useForm<IOrderDeliveryForm>({
        mode: 'onChange',
        defaultValues: {
            address: "",
            phone_number: phone ?? "",
            date: business_profile && isScheduleRequired
                ? getNextOrderDate(business_profile)
                : moment().format('YYYY-MM-DD'),
            time: undefined,
            notes: "",
            cash: true,
            card: false,
        },
    });

    useOrderScheduleValidation({
        business_profile,
        watch,
        setValue,
        setError,
        clearErrors,
        enabled: isScheduleRequired,
    });

    const { getPaymentParams } = useOrderPayment(pos_terminal, setValue, watch, getValues);
    const totalDeliveryCost = calculatedDeliveryCost + normalizedPackageFee + normalizedTotalPrice;

    const handleNote = (note: string) => {
        setValue('notes', note.trim() || undefined);
        onCloseNoteDialog();
    };

    const onOrder = async () => {
        const address = getValues('address');
        const phoneNumber = getValues('phone_number').trim().replace(/\s+/g, '');

        if (address.length < 5) {
            setError("address", { message: "" });
            dialogRef.current?.scrollTo({ top: 0, behavior: "smooth" });
            return;
        } else if (phoneNumber.length < 12) {
            setError("phone_number", { message: "" });
            dialogRef.current?.scrollTo({ top: 0, behavior: "smooth" });
            return;
        }

        const orderDate = getValues('date');
        const orderTime = getValues('time');

        if (isScheduleRequired) {
            if (!orderTime) {
                setError('time', { type: 'required', message: '' });
                dialogRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
                return;
            }

            if (business_profile && orderDate && isBusinessFreeDay(orderDate, business_profile.free_days)) {
                setError('date', { message: t(getOrderTimeValidationMessageKey('closed_day')) });
                dialogRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
                return;
            }

            const timeValidation = validateOrderDateTime(orderDate, orderTime, business_profile);
            if (!timeValidation.valid) {
                setError('time', { message: t(getOrderTimeValidationMessageKey(timeValidation.reason)) });
                dialogRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
                return;
            }
        } else if (orderTime) {
            if (business_profile && orderDate && isBusinessFreeDay(orderDate, business_profile.free_days)) {
                setError('date', { message: t(getOrderTimeValidationMessageKey('closed_day')) });
                dialogRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
                return;
            }

            const timeValidation = validateOrderDateTime(orderDate, orderTime, business_profile);
            if (!timeValidation.valid) {
                setError('time', { message: t(getOrderTimeValidationMessageKey(timeValidation.reason)) });
                dialogRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
                return;
            }
        }

        const data: IDeliveryOrderData = {
            cart: cart,
            delivery_cost: calculatedDeliveryCost,
            packageFee: normalizedPackageFee,
            total_cost: totalDeliveryCost,
        };

        const params: IOrderParams = {
            phone_number: phoneNumber,
            notes: getValues('notes'),
            address: address,
            date: getValues('date'),
            time: getValues('time'),
            ...getPaymentParams(),
            totalItems: totalItemCount,
            company_id: companyId,
            company_name: company_name,
            company_logo: company_logo,
            company_contacts: company_contacts
        };

        const payload: INewOrder = {
            company_id: companyId,
            order_type: OrderTypes.DELIVERY,
            phone_number: phoneNumber,
            uuid: user_id,
            data: JSON.stringify(data),
            order_parameters: JSON.stringify(params)
        };
        try {
            await sendOrder(payload).unwrap();
            setMainCart([]);
            setDialog(
                <OrderSuccess
                    setContentType={setContentType}
                    onClose={onCloseDialog}
                />)
        } catch (error: any) {
            const status = error?.error;
            if (status === 419) {
                setDialog(
                    <SmsCodeDialog
                        data={payload}
                        setDialog={setDialog}
                        setMainCart={setMainCart}
                        onCloseDialog={onCloseDialog}
                        setContentType={setContentType}
                    />
                )
            } else {
                showToastError(t('Toast.Error.Common'));
            }
        }
    };

    return {
        open,
        errors,
        control,
        dialogRef,
        currentDate,
        newOrderLoading,
        totalDeliveryCost,
        calculatedDeliveryCost,
        isScheduleRequired,
        onOrder,
        register,
        getValues,
        watch,
        handleNote,
        onOpenNoteDialog,
        onCloseNoteDialog,
        setValue,
    }
};

export default useOrderDelivery
